import { useEffect, useMemo, useState } from "react";
import { Search, Users as UsersIcon, Pin, Activity, ShieldCheck, FileText, Smartphone, Upload, Loader2, X, AlertTriangle, CheckCircle2, Download, Filter } from "lucide-react";
import api, { fmtINR, formatDetail, shortDate } from "@/lib/api";
import Modal from "@/components/Modal";
import PinChannelButton from "@/components/PinChannelButton";
import { buildWebsiteColorMap } from "@/lib/websiteColors";

const CATEGORY_LABELS = {
    0: "FTD",
    1: "2nd",
    2: "3rd",
    3: "4th",
    4: "5+",
};

const VERIFY_LABELS = {
    sms: { label: "SMS", icon: Smartphone, cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
    bank_statement: { label: "Statement", icon: FileText, cls: "bg-blue-50 text-blue-700 ring-blue-200" },
    manual: { label: "Manual", icon: ShieldCheck, cls: "bg-slate-100 text-slate-600 ring-slate-200" },
};

/**
 * Customers page — admin & partner. Lists every customer who has paid at least
 * once. Sourced from db.customer_deposits enriched with active overrides.
 *
 * @param {"admin"|"partner"} scope
 */
export default function CustomersPage({ scope = "partner" }) {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("recent");
    const [partnerFilter, setPartnerFilter] = useState("");
    const [partners, setPartners] = useState([]);
    const [websites, setWebsites] = useState([]);   // dropdown options
    const [websiteFilter, setWebsiteFilter] = useState("");        // "" = all, "__untagged__" = legacy
    const [categoryFilter, setCategoryFilter] = useState("");      // "" = all, "0".."4"
    const [verificationFilter, setVerificationFilter] = useState(""); // "" | "sms" | "bank_statement" | "manual"
    const [pinnedOnly, setPinnedOnly] = useState(false);
    const [loading, setLoading] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);

    const hasActiveFilters = !!(websiteFilter || categoryFilter !== "" || verificationFilter || pinnedOnly);
    const clearFilters = () => {
        setWebsiteFilter("");
        setCategoryFilter("");
        setVerificationFilter("");
        setPinnedOnly(false);
    };

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ sort, limit: "200" });
            if (q) params.set("q", q);
            if (scope === "admin" && partnerFilter) params.set("partner_id", partnerFilter);
            if (websiteFilter) params.set("website_id", websiteFilter);
            if (categoryFilter !== "") params.set("category", categoryFilter);
            if (verificationFilter) params.set("verification", verificationFilter);
            if (pinnedOnly) params.set("pinned_only", "true");
            const url = scope === "admin" ? `/admin/customers?${params}` : `/partner/customers?${params}`;
            const r = await api.get(url);
            setItems(r.data.items || []);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (scope === "admin") {
            api.get("/admin/partners").then((r) => setPartners(r.data.items || [])).catch(() => {});
        }
        // Websites are partner-scoped — only load for partner view.
        if (scope === "partner") {
            api.get("/partner/websites").then((r) => setWebsites(r.data.items || [])).catch(() => {});
        }
    }, [scope]);

    useEffect(() => {
        const t = setTimeout(load, q ? 250 : 0);
        return () => clearTimeout(t);
         
    }, [q, sort, partnerFilter, scope, websiteFilter, categoryFilter, verificationFilter, pinnedOnly]);

    // Per-website colour map — same lookup used on Successful Txns &
    // Approvals, so the same site keeps the same colour across the
    // entire agent workspace.
    const colorOf = useMemo(() => buildWebsiteColorMap(items), [items]);

    const stats = useMemo(() => ({
        total: items.length,
        repeat: items.filter((c) => c.success_count > 1).length,
        pinned: items.filter((c) => c.pinned_channel_id).length,
    }), [items]);

    return (
        <div className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                        <UsersIcon className="w-6 h-6 text-blue-600" /> Customers
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">Everyone who has ever successfully deposited. Pin a channel directly from here for VIPs.</p>
                </div>
                <div className="flex gap-3">
                    <Stat label="Total" value={stats.total} />
                    <Stat label="Repeat" value={stats.repeat} accent="emerald" />
                    <Stat label="Pinned" value={stats.pinned} accent="blue" />
                </div>
            </div>

            <div className="card p-4 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input data-testid="customers-search"
                        placeholder="Search by username…"
                        value={q} onChange={(e) => setQ(e.target.value)}
                        className="input pl-9" />
                </div>
                {scope === "admin" && (
                    <select data-testid="customers-partner-filter" value={partnerFilter}
                        onChange={(e) => setPartnerFilter(e.target.value)} className="input w-56">
                        <option value="">All partners</option>
                        {partners.map((p) => <option key={p.partner_id} value={p.partner_id}>{p.business_name}</option>)}
                    </select>
                )}
                <select data-testid="customers-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="input w-44">
                    <option value="recent">Most recent</option>
                    <option value="top">Top depositors</option>
                    <option value="username">By username</option>
                </select>
                <button data-testid="customers-bulk-import-btn" onClick={() => setBulkOpen(true)}
                        className="btn-secondary px-3 py-2 rounded-md text-xs flex items-center gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50">
                    <Upload className="w-3.5 h-3.5" /> Bulk import
                </button>
                {loading && <span className="text-xs text-slate-400">loading…</span>}
            </div>

            {/* Filter row — kept on a separate card from search/sort so
                the most-used controls (search box) stay uncluttered.
                Filters compose AND, server-side. The "Clear" button
                appears only when at least one filter is active. */}
            <div className="card p-4 flex flex-wrap items-center gap-3" data-testid="customers-filter-row">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-medium pr-1">
                    <Filter className="w-3.5 h-3.5" /> Filter
                </div>

                {/* Website — partner-only (admins span all partners) */}
                {scope === "partner" && (
                    <select data-testid="customers-website-filter" value={websiteFilter}
                        onChange={(e) => setWebsiteFilter(e.target.value)}
                        className="input w-48 text-sm" title="Filter by website">
                        <option value="">All websites</option>
                        {websites.map((w) => (
                            <option key={w.website_id} value={w.website_id}>{w.name}</option>
                        ))}
                        <option value="__untagged__">— Untagged (legacy) —</option>
                    </select>
                )}

                {/* Deposit category */}
                <select data-testid="customers-category-filter" value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="input w-40 text-sm" title="Filter by deposit count">
                    <option value="">All categories</option>
                    <option value="0">FTD (1st)</option>
                    <option value="1">2nd</option>
                    <option value="2">3rd</option>
                    <option value="3">4th</option>
                    <option value="4">5+ (VIP)</option>
                </select>

                {/* Verification method — last successful deposit's method */}
                <select data-testid="customers-verification-filter" value={verificationFilter}
                    onChange={(e) => setVerificationFilter(e.target.value)}
                    className="input w-40 text-sm" title="Filter by last verification">
                    <option value="">All verification</option>
                    <option value="sms">SMS</option>
                    <option value="bank_statement">Statement</option>
                    <option value="manual">Manual</option>
                </select>

                {/* Pinned-only toggle */}
                <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer select-none">
                    <input type="checkbox" data-testid="customers-pinned-only"
                        checked={pinnedOnly} onChange={(e) => setPinnedOnly(e.target.checked)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                    <Pin className="w-3.5 h-3.5 text-blue-600" /> Pinned only
                </label>

                {hasActiveFilters && (
                    <button data-testid="customers-clear-filters"
                        onClick={clearFilters}
                        className="btn-secondary text-xs px-2.5 py-1 rounded-md flex items-center gap-1 text-slate-500 hover:text-slate-700 ml-auto">
                        <X className="w-3 h-3" /> Clear filters
                    </button>
                )}
            </div>

            {/* Per-website colour legend — same chip + initial badge
                treatment as Successful Txns & Approvals so the agent's
                colour-to-website memory transfers across pages. */}
            {(() => {
                const names = Array.from(new Set(items.map((c) => c.website_name).filter(Boolean))).sort((a, b) => a.localeCompare(b));
                if (names.length < 2) return null;
                return (
                    <div data-testid="customers-color-legend" className="flex flex-wrap items-center gap-2 px-1 text-[11px]">
                        <span className="text-slate-400 uppercase tracking-wider font-medium">Legend:</span>
                        {names.map((n) => {
                            const c = colorOf(n);
                            return (
                                <span key={n} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border font-semibold ${c.pill}`}>
                                    <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: c.stripe }} />
                                    {c.initial && (
                                        <span className="font-mono text-[9px] tracking-wider opacity-70">
                                            {c.initial}
                                        </span>
                                    )}
                                    {n}
                                </span>
                            );
                        })}
                    </div>
                );
            })()}

            <div className="card overflow-hidden">
                {items.length === 0 ? (
                    <div className="px-4 py-16 text-center text-sm text-slate-400" data-testid="customers-empty">
                        No customers yet. They&apos;ll appear here after their first successful deposit.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="px-4 py-2.5 text-left">Customer</th>
                                {scope === "admin" && <th className="px-4 py-2.5 text-left">Partner</th>}
                                <th className="px-4 py-2.5 text-left">Website</th>
                                <th className="px-4 py-2.5 text-left">Deposits</th>
                                <th className="px-4 py-2.5 text-right">Last amount</th>
                                <th className="px-4 py-2.5 text-left">Last channel</th>
                                <th className="px-4 py-2.5 text-left">Last seen</th>
                                <th className="px-4 py-2.5 text-left">Pinned</th>
                                <th className="px-4 py-2.5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => {
                                const verify = VERIFY_LABELS[c.last_verification];
                                const VerifyIcon = verify?.icon;
                                // Per-website colour — same treatment as
                                // the rest of the agent workspace.
                                const ws = colorOf(c.website_name);
                                return (
                                    <tr key={`${c.partner_id}-${c.customer_user_id}`}
                                        data-testid={`customer-row-${c.customer_user_id}`}
                                        style={{ backgroundColor: ws.tint }}
                                        className="border-b border-slate-100 last:border-0 hover:brightness-95 transition-all align-middle">
                                        <td className="px-4 py-3 border-l-[6px]" style={{ borderLeftColor: ws.stripe }}>
                                            <div className="font-medium text-slate-900">{c.username || <span className="text-slate-400 italic">anon</span>}</div>
                                            <div className="text-[10px] font-mono text-slate-400">{c.customer_user_id}</div>
                                        </td>
                                        {scope === "admin" && (
                                            <td className="px-4 py-3 text-xs text-slate-600">{c.partner_business_name || c.partner_id}</td>
                                        )}
                                        <td className="px-4 py-3 text-xs">
                                            {c.website_name ? (
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold truncate max-w-[160px] ${ws.pill}`} title={c.website_name}>
                                                    {ws.initial && (
                                                        <span className="font-mono text-[9px] tracking-wider opacity-70 border-r pr-1 mr-0.5 border-current/30">
                                                            {ws.initial}
                                                        </span>
                                                    )}
                                                    {c.website_name}
                                                </span>
                                            ) : (
                                                <span className="text-slate-300 text-[11px]">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono font-semibold text-emerald-700">{c.success_count}</span>
                                                <span className="pill pill-muted">{CATEGORY_LABELS[c.category] || `${c.category}+`}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono">{c.last_deposit_amount ? fmtINR(c.last_deposit_amount) : "—"}</td>
                                        <td className="px-4 py-3">
                                            <div className="text-xs text-slate-700 truncate max-w-[160px]">{c.last_channel_label || "—"}</div>
                                            {verify && (
                                                <span className={`pill ring-1 ${verify.cls} inline-flex items-center gap-1 mt-0.5`}>
                                                    <VerifyIcon className="w-3 h-3" /> {verify.label}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-500">{shortDate(c.last_deposit_at)}</td>
                                        <td className="px-4 py-3">
                                            {c.pinned_channel_id ? (
                                                <div className="flex items-center gap-1.5">
                                                    <Pin className="w-3.5 h-3.5 text-blue-600" />
                                                    <div>
                                                        <div className="text-xs font-medium text-blue-900 truncate max-w-[150px]">{c.pinned_channel_label || c.pinned_channel_id}</div>
                                                        <div className="text-[10px] text-blue-600 uppercase tracking-wide">{c.pinned_mode}</div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-slate-400">— smart routing —</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end">
                                                <PinChannelButton
                                                    customerUsername={c.username}
                                                    customerUserId={c.customer_user_id}
                                                    partnerId={c.partner_id}
                                                    scope={scope}
                                                    label={c.pinned_channel_id ? "Edit pin" : "Pin channel"}
                                                    onPinned={load}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {bulkOpen && (
                <BulkImportModal
                    scope={scope}
                    partners={partners}
                    defaultPartnerId={scope === "admin" ? partnerFilter : ""}
                    onClose={() => setBulkOpen(false)}
                    onDone={() => { setBulkOpen(false); load(); }}
                />
            )}
        </div>
    );
}

function BulkImportModal({ scope, partners, defaultPartnerId, onClose, onDone }) {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState("");
    const [partnerId, setPartnerId] = useState(defaultPartnerId || "");
    // iter-70: two-phase progress. `uploadPct` tracks the byte-level
    // upload (0-100) exposed by axios onUploadProgress. Once the browser
    // finishes streaming the file, we flip to `processing=true` and show
    // an indeterminate "Processing N rows…" pill until the server
    // returns the summary. We can't get exact per-row progress without
    // SSE, but this covers the two long phases users actually wait on.
    const [uploadPct, setUploadPct] = useState(0);
    const [processing, setProcessing] = useState(false);
    const [rowEstimate, setRowEstimate] = useState(0);

    // Rough row-count estimator: newlines in the picked file. Used only
    // for the "Processing ~X rows…" label so we don't need to parse the
    // CSV client-side. Runs once when a file is picked.
    const estimateRows = async (f) => {
        try {
            const text = await f.text();
            const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
            setRowEstimate(Math.max(0, lines.length - 1));  // minus header
        } catch { setRowEstimate(0); }
    };

    const onFilePicked = (f) => {
        setFile(f);
        setErr("");
        setUploadPct(0);
        setProcessing(false);
        if (f) estimateRows(f);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!file) { setErr("Pick a CSV file first"); return; }
        if (scope === "admin" && !partnerId) { setErr("Select which partner this CSV belongs to"); return; }
        setBusy(true); setErr(""); setResult(null);
        setUploadPct(0); setProcessing(false);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const url = scope === "admin"
                ? `/admin/customers/bulk-upload?partner_id=${encodeURIComponent(partnerId)}`
                : "/partner/customers/bulk-upload";
            // Belt-and-suspenders: explicitly set Content-Type to
            // undefined for THIS call. Axios + browser will then set the
            // correct multipart/form-data with boundary. Prevents the
            // 422 "body.file missing" we saw when a proxy or cached JS
            // stripped the boundary. Also bumps timeout to 5 min for
            // 10k-row imports.
            const r = await api.post(url, fd, {
                headers: { "Content-Type": undefined },
                timeout: 300_000,
                transformRequest: (data) => data,  // don't let axios JSON-stringify FormData
                onUploadProgress: (evt) => {
                    if (evt.total) {
                        const pct = Math.round((evt.loaded / evt.total) * 100);
                        setUploadPct(pct);
                        if (pct >= 100) setProcessing(true);
                    }
                },
            });
            setResult(r.data);
        } catch (e2) {
            const status = e2?.response?.status;
            const detail = formatDetail(e2?.response?.data?.detail);
            if (detail === "Something went wrong." || !detail) {
                if (status === 504 || status === 502) {
                    setErr(`Server timed out processing this CSV (HTTP ${status}). Split the file into smaller batches (< 2000 rows) and retry.`);
                } else if (status === 422) {
                    setErr("Upload was rejected by the server (HTTP 422). Hard-refresh the page (Ctrl/Cmd+Shift+R) and retry — your browser may have cached an older upload script.");
                } else if (status) {
                    setErr(`Upload failed (HTTP ${status}). Check the file format and retry.`);
                } else {
                    setErr(e2.message || "Network error. Check your connection and retry.");
                }
            } else {
                setErr(detail);
            }
        } finally {
            setBusy(false);
            setProcessing(false);
        }
    };

    // iter-70: pull the sample CSV from the server so it's populated
    // with THIS partner's real link_code — merchant can download,
    // fill in customer_name + deposit_count, and re-upload without
    // hand-editing placeholder link codes. Falls back to a hardcoded
    // sample if the endpoint is unavailable (e.g. admin scope, offline).
    const downloadSample = async () => {
        try {
            let blob;
            if (scope === "partner") {
                const r = await api.get("/partner/customers/bulk-sample", { responseType: "blob" });
                blob = r.data;
            } else {
                // Admin scope doesn't have a partner-specific link, so keep the placeholder sample.
                const csv = "customer_name,pay_link,deposit_count\njohn_doe,YOUR_LINK_CODE,5\njane_smith,YOUR_LINK_CODE,0\nvip_alice,YOUR_LINK_CODE,12\n";
                blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
            }
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "customers-sample.csv";
            a.click();
            URL.revokeObjectURL(a.href);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || "Could not download sample. Try again.");
        }
    };

    return (
        <Modal testId="customers-bulk-modal" onClose={onClose}
                title="Bulk import customers" size="md">
            {!result ? (
                <form onSubmit={submit} className="space-y-3 text-sm">
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-slate-700">
                        Upload a CSV with three columns:
                        <div className="font-mono mt-1 text-[11px] bg-white border border-slate-200 rounded px-2 py-1.5">
                            customer_name, pay_link, deposit_count
                        </div>
                        <ul className="text-[11px] text-slate-600 mt-2 list-disc pl-4 space-y-0.5">
                            <li><span className="font-mono">pay_link</span> = the short link code (e.g. <span className="font-mono">gvm1mqayvr</span>) — must be one of your links</li>
                            <li><span className="font-mono">deposit_count</span> = how many successful deposits they&apos;ve already made</li>
                            <li>Existing customers: their count is <strong>replaced</strong> with the CSV value (CSV 5 → new count = 5, whatever the old value was)</li>
                            <li>Same customer listed twice in the CSV: the <strong>last row</strong> wins</li>
                            <li>Max <strong>10,000</strong> rows per file</li>
                        </ul>
                        <button type="button" onClick={downloadSample} data-testid="bulk-sample"
                                className="text-blue-700 hover:underline mt-2 inline-flex items-center gap-1 text-xs">
                            <Download className="w-3 h-3" /> Download sample CSV
                        </button>
                    </div>

                    {scope === "admin" && (
                        <div>
                            <label className="label">Partner <span className="text-rose-500">*</span></label>
                            <select data-testid="bulk-partner-select" required value={partnerId}
                                    onChange={(e) => setPartnerId(e.target.value)} className="input">
                                <option value="">Select partner…</option>
                                {partners.map((p) => (
                                    <option key={p.partner_id} value={p.partner_id}>{p.business_name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="label">CSV file</label>
                        <input data-testid="bulk-file-input" type="file" accept=".csv,text/csv,application/vnd.ms-excel"
                                onChange={(e) => onFilePicked(e.target.files?.[0] || null)}
                                className="input" />
                        {file && <div className="text-[11px] text-slate-500 mt-1 font-mono">
                            {file.name} · {(file.size / 1024).toFixed(1)} KB
                            {rowEstimate > 0 && ` · ~${rowEstimate.toLocaleString()} rows`}
                        </div>}
                    </div>

                    {busy && (
                        <div data-testid="bulk-progress" className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
                            {!processing ? (
                                <>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="font-medium flex items-center gap-1.5">
                                            <Upload className="w-3.5 h-3.5" /> Uploading CSV…
                                        </span>
                                        <span data-testid="bulk-upload-pct" className="font-mono">{uploadPct}%</span>
                                    </div>
                                    <div className="h-1.5 bg-blue-100 rounded overflow-hidden">
                                        <div className="h-full bg-blue-500 transition-all duration-150"
                                             style={{ width: `${uploadPct}%` }} />
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    <span data-testid="bulk-processing" className="font-medium">
                                        Processing {rowEstimate > 0 ? `~${rowEstimate.toLocaleString()} rows` : "your file"}…
                                        <span className="text-blue-700/70 ml-1">this usually takes a few seconds.</span>
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {err && (
                        <div data-testid="bulk-error" className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700 flex items-start gap-2">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {err}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                        <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 rounded-md">Cancel</button>
                        <button data-testid="bulk-submit" type="submit" disabled={busy || !file}
                                className="btn-primary px-4 py-2 rounded-md flex items-center gap-2">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            Import
                        </button>
                    </div>
                </form>
            ) : (
                <ImportSummary result={result} sourceFile={file}
                                onClose={() => onDone()} onAnother={() => setResult(null)} />
            )}
        </Modal>
    );
}

function ImportSummary({ result, sourceFile, onClose, onAnother }) {
    const [downloading, setDownloading] = useState(false);

    // iter-70: reconstruct a "failed rows" CSV client-side by reading
    // the original file the user just uploaded, extracting the exact
    // rows that failed (via the row-number the server returned), and
    // appending an `error_reason` column. Merchant can then fix and
    // re-upload only the rejects instead of hunting through a big file.
    const downloadFailed = async () => {
        if (!sourceFile) return;
        setDownloading(true);
        try {
            const text = await sourceFile.text();
            const lines = text.split(/\r?\n/);
            // Row numbers in the response are 1-indexed against the
            // ORIGINAL file (header = row 1, first data row = 2). This
            // matches `parse_csv`'s `enumerate(reader, start=2)`.
            const failedRows = result.all_failed || result.samples?.failed || [];
            const reasonByRow = new Map();
            for (const f of failedRows) {
                if (typeof f.row === "number") reasonByRow.set(f.row, f.reason || "");
            }
            const header = (lines[0] || "customer_name,pay_link,deposit_count").replace(/\r$/, "");
            const out = [header + ",error_reason"];
            for (const [rowNum, reason] of reasonByRow) {
                const line = (lines[rowNum - 1] || "").replace(/\r$/, "");
                if (!line) continue;
                // Escape reason: wrap in quotes and double any internal quotes.
                const safeReason = `"${(reason || "").replace(/"/g, '""')}"`;
                out.push(`${line},${safeReason}`);
            }
            const blob = new Blob([out.join("\n") + "\n"], { type: "text/csv;charset=utf-8" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            const stamp = new Date().toISOString().slice(0, 10);
            a.download = `failed-rows-${stamp}.csv`;
            a.click();
            URL.revokeObjectURL(a.href);
        } finally { setDownloading(false); }
    };

    return (
        <div className="space-y-4 text-sm" data-testid="bulk-summary">
            <div className="grid grid-cols-3 gap-3">
                <SummaryStat label="Inserted" value={result.inserted} accent="emerald" icon={CheckCircle2} testId="bulk-inserted" />
                <SummaryStat label="Updated" value={result.updated || 0} accent="blue" icon={Upload} testId="bulk-updated" />
                <SummaryStat label="Failed" value={result.failed} accent="rose" icon={AlertTriangle} testId="bulk-failed" />
            </div>
            <div className="text-[11px] text-slate-500">Of {result.total} non-empty rows in your file.</div>

            {result.failed > 0 && (
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <div className="text-xs font-semibold text-slate-700">
                            Rows that failed (fix and re-upload):
                        </div>
                        {sourceFile && (
                            <button type="button" onClick={downloadFailed} disabled={downloading}
                                    data-testid="bulk-download-failed"
                                    className="text-rose-700 hover:text-rose-900 hover:underline inline-flex items-center gap-1 text-[11px] font-medium">
                                {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                Download {result.failed} failed row{result.failed === 1 ? "" : "s"} (CSV)
                            </button>
                        )}
                    </div>
                    <div className="border border-rose-100 rounded max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-rose-50 text-rose-800 text-[10px] uppercase tracking-wider">
                                <tr><th className="px-2 py-1 text-left">Row</th><th className="px-2 py-1 text-left">Reason</th></tr>
                            </thead>
                            <tbody>
                                {(result.samples?.failed || []).map((f, i) => (
                                    <tr key={f.row != null ? `f-${f.row}-${i}` : `f-${i}`} className="border-t border-rose-50">
                                        <td className="px-2 py-1 font-mono text-slate-500">{f.row}</td>
                                        <td className="px-2 py-1 text-rose-700">{f.reason}</td>
                                    </tr>
                                ))}
                                {result.failed > (result.samples?.failed || []).length && (
                                    <tr><td colSpan={2} className="px-2 py-1 text-[10px] text-slate-500 italic text-center">
                                        …{result.failed - (result.samples?.failed || []).length} more — use "Download {result.failed} failed rows" to see all.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {(result.updated || 0) > 0 && (
                <div>
                    <div className="text-xs font-semibold text-slate-700 mb-1.5">Existing customers — deposit count updated:</div>
                    <div className="border border-blue-100 rounded max-h-32 overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-blue-50 text-blue-800 text-[10px] uppercase tracking-wider">
                                <tr>
                                    <th className="px-2 py-1 text-left">Customer</th>
                                    <th className="px-2 py-1 text-right">New count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(result.samples?.updated || []).map((u, i) => (
                                    <tr key={u.username ? `u-${u.username}-${i}` : `u-${i}`} className="border-t border-blue-50">
                                        <td className="px-2 py-1 font-medium">{u.username}</td>
                                        <td className="px-2 py-1 text-right font-mono font-semibold text-blue-700">{u.to ?? "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={onAnother} className="btn-secondary px-4 py-2 rounded-md">Upload another</button>
                <button onClick={onClose} data-testid="bulk-done" className="btn-primary px-4 py-2 rounded-md">Done</button>
            </div>
        </div>
    );
}

function SummaryStat({ label, value, accent, icon: Icon, testId }) {
    const cls = accent === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : accent === "rose" ? "bg-rose-50 text-rose-700 border-rose-200"
        : "bg-slate-50 text-slate-600 border-slate-200";
    return (
        <div data-testid={testId} className={`border rounded-md p-3 text-center ${cls}`}>
            <Icon className="w-4 h-4 mx-auto opacity-70" />
            <div className="text-2xl font-bold mt-1 leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-wider opacity-70 mt-1">{label}</div>
        </div>
    );
}

function Stat({ label, value, accent }) {
    const cls = accent === "emerald" ? "text-emerald-700 bg-emerald-50"
        : accent === "blue" ? "text-blue-700 bg-blue-50"
        : "text-slate-700 bg-slate-50";
    return (
        <div className={`px-4 py-2 rounded-lg ${cls} flex flex-col items-center min-w-[68px]`}>
            <Activity className="w-3 h-3 opacity-50" />
            <div className="text-base font-bold leading-tight">{value}</div>
            <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
        </div>
    );
}
